/**
 * PageReporter — sayfa kayit defteri ve (acikse) AI analizi icin icerik yukleyici.
 *
 * Akis:
 *   page_view -> load + DOM sessizligi -> idle -> yerel hafiza -> page-status ->
 *   gerekiyorsa POST (meta | full | confirm)
 *
 * Ayri endpoint/topic kullanilir; event batch'ine dokunulmaz. Sunucu tarafinda
 * her sayfa bir kez analiz edilir (pk + icerik hash'i + teyit mekanizmasi).
 */

import log2 from "../../../js/log2-lite";
import trackerConfig from "../config";
import identity from "../identity";
import { resolvePageKey } from "./pageKey";
import { collectMeta, isNoIndex } from "./meta";
import { extractContent } from "./content";
import { structuralHash, contentHash } from "./hash";

const log = log2("TRACKER-PAGES");

// Hesap/odeme/oturum gibi kisisel sayfalar hicbir kosulda yuklenmez
const SENSITIVE_PATH_PATTERNS = [
    "/account",
    "/hesap",
    "/checkout",
    "/odeme",
    "/payment",
    "/cart",
    "/sepet",
    "/order",
    "/siparis",
    "/login",
    "/giris",
    "/signin",
    "/register",
    "/kayit",
    "/admin",
    "/profile",
    "/profil",
    "/my-account",
];

const MEMO_KEY_PREFIX = "supsis-wa-pg:";
const MEMO_LIMIT = 300;
const MEMO_TTL_MS = 7 * 24 * 60 * 60 * 1000;
const MAX_UPLOADS_PER_SESSION = 10;

const DOM_QUIET_MS = 1000;
const DOM_MAX_WAIT_MS = 6000;
// Sunucu "wait" dediginde (baska ziyaretci yukluyor, slot 120 sn) bu kadar sonra tekrar sorulur
const WAIT_RETRY_MS = 130 * 1000;

function now() {
    return Date.now();
}

function readMemo(siteId) {
    try {
        const raw = window.localStorage.getItem(MEMO_KEY_PREFIX + siteId);
        const parsed = raw ? JSON.parse(raw) : null;
        return parsed && typeof parsed === "object" ? parsed : {};
    } catch (e) {
        return {};
    }
}

function writeMemo(siteId, memo) {
    try {
        const keys = Object.keys(memo);
        // LRU: en eski kayitlari at
        if (keys.length > MEMO_LIMIT) {
            keys.sort(function (a, b) {
                return (memo[a][2] || 0) - (memo[b][2] || 0);
            });
            for (let i = 0; i < keys.length - MEMO_LIMIT; i++) delete memo[keys[i]];
        }
        window.localStorage.setItem(MEMO_KEY_PREFIX + siteId, JSON.stringify(memo));
    } catch (e) {
        /* kota dolu / private mode: hafiza olmadan da calisir */
    }
}

function onIdle(callback) {
    if (typeof window.requestIdleCallback === "function") {
        window.requestIdleCallback(callback, { timeout: 4000 });
    } else {
        setTimeout(callback, 600);
    }
}

/** DOM degisiklikleri durana kadar (veya ust sinira kadar) bekler. */
function whenDomQuiet(callback) {
    if (typeof window.MutationObserver !== "function") {
        setTimeout(callback, DOM_QUIET_MS);
        return;
    }

    let quietTimer = null;
    let done = false;
    const observer = new MutationObserver(function () {
        if (quietTimer) clearTimeout(quietTimer);
        quietTimer = setTimeout(finish, DOM_QUIET_MS);
    });

    const finish = function () {
        if (done) return;
        done = true;
        if (quietTimer) clearTimeout(quietTimer);
        try {
            observer.disconnect();
        } catch (e) {
            /* yoksay */
        }
        callback();
    };

    try {
        observer.observe(document.body || document.documentElement, { childList: true, subtree: true });
    } catch (e) {
        setTimeout(finish, DOM_QUIET_MS);
        return;
    }

    quietTimer = setTimeout(finish, DOM_QUIET_MS);
    setTimeout(finish, DOM_MAX_WAIT_MS);
}

function whenLoaded(callback) {
    if (document.readyState === "complete") {
        callback();
        return;
    }
    window.addEventListener("load", function handler() {
        window.removeEventListener("load", handler);
        callback();
    });
}

class PageReporter {
    constructor(tracker) {
        this.tracker = tracker;
        this.siteId = tracker.siteId;
        this.uploads = 0;
        this.inFlight = false;
        this.lastPk = "";
        this.waitingVisible = false;
        this.waitRetryTimer = null;
    }

    settings() {
        return trackerConfig.pages || {};
    }

    isAiEnabled() {
        const pages = this.settings();
        return !!(pages.ai && pages.ai.enabled);
    }

    /**
     * Icerik (markdown) toplanacak mi.
     * AI analizi kapali olsa bile site "icerik arsivi" acik tutabilir; arsiv
     * ucretsizdir ve ileride chatbot bilgi bankasini besler.
     */
    isContentEnabled() {
        const pages = this.settings();
        return this.isAiEnabled() || pages.storeContent !== false;
    }

    /** Sayfa hicbir kosulda yuklenmemeli mi. */
    isSkipped(path) {
        const pages = this.settings();
        if (pages.collectMeta === false) return "meta-disabled";
        if (trackerConfig.isExcludedPath(path)) return "excluded";

        const lower = String(path || "").toLowerCase();
        for (let i = 0; i < SENSITIVE_PATH_PATTERNS.length; i++) {
            if (lower.indexOf(SENSITIVE_PATH_PATTERNS[i]) !== -1) return "sensitive";
        }

        const extra = pages.excludedPaths || [];
        for (let i = 0; i < extra.length; i++) {
            if (extra[i] && lower.indexOf(String(extra[i]).toLowerCase()) === 0) return "excluded";
        }

        try {
            if (document.querySelector('input[type="password"]')) return "password-form";
        } catch (e) {
            /* yoksay */
        }

        if (pages.respectNoindex !== false && isNoIndex()) return "noindex";
        return "";
    }

    /** page_view sonrasi cagrilir (ilk yukleme ve SPA gecisleri). */
    schedule() {
        if (this.uploads >= MAX_UPLOADS_PER_SESSION) return;
        if (this.inFlight) return;

        const self = this;
        whenLoaded(function () {
            whenDomQuiet(function () {
                onIdle(function () {
                    self.run().catch(function (error) {
                        log.debug("page report failed", error);
                    });
                });
            });
        });
    }

    /**
     * Arka planda acilan sekme (Cmd/Ctrl+tik, "yeni sekmede ac"): sayfa gorunur
     * olana kadar bekler, sonra raporu yeniden planlar. Daha once "hidden" ile
     * tamamen atlaniyor ve sekmeye donulunce bir daha sorulmuyordu; sayfa ancak
     * yenilenince kaydediliyordu.
     */
    deferUntilVisible() {
        if (this.waitingVisible) return;
        this.waitingVisible = true;
        const self = this;
        document.addEventListener("visibilitychange", function handler() {
            if (document.visibilityState === "hidden") return;
            document.removeEventListener("visibilitychange", handler);
            self.waitingVisible = false;
            log.debug("page visible, rescheduling report");
            self.schedule();
        });
    }

    /** Sunucu "wait" dedi: slot suresi dolunca ayni sayfa icin tekrar sorulur. */
    retryAfterWait() {
        if (this.waitRetryTimer) return;
        const self = this;
        this.waitRetryTimer = setTimeout(function () {
            self.waitRetryTimer = null;
            self.schedule();
        }, WAIT_RETRY_MS);
    }

    async run() {
        if (this.inFlight || this.uploads >= MAX_UPLOADS_PER_SESSION) return;

        if (document.visibilityState === "hidden") {
            log.debug("page report deferred: tab hidden");
            this.deferUntilVisible();
            return;
        }

        const pages = this.settings();
        const key = resolvePageKey(pages.keepQueryParams || []);
        if (!key) return;

        const skipReason = this.isSkipped(key.path);
        if (skipReason) {
            log.debug("page skipped", skipReason, key.path);
            return;
        }

        this.inFlight = true;
        try {
            const collected = collectMeta();
            const sh = structuralHash(collected.meta, collected.jsonLd);

            const contentEnabled = this.isContentEnabled();
            let content = null;
            let ch = "";
            if (contentEnabled) {
                content = extractContent({
                    contentSelector: pages.contentSelector,
                    removeSelectors: pages.removeSelectors,
                    maskSelectors: trackerConfig.maskSelectors,
                    maxBytes: pages.maxContentBytes,
                });
                if (content) ch = contentHash(content.md);
            }

            // 1) Yerel hafiza: ayni imzalarla 7 gun icinde gonderildiyse aga cikma
            const memo = readMemo(this.siteId);
            const remembered = memo[key.pk];
            if (
                remembered &&
                remembered[0] === sh &&
                remembered[1] === ch &&
                now() - (remembered[2] || 0) < MEMO_TTL_MS
            ) {
                return;
            }

            // 2) Sunucu durumu (CDN cache'li)
            const status = await this.fetchStatus(key.pk);
            if (!status) return;

            // "wait" gecici bir durumdur (baska ziyaretci yukluyor); hafizaya alinmaz,
            // aksi halde yuklemesi basarisiz olan sayfa 7 gun boyunca hic sorulmazdi
            if (status.s === "wait") {
                log.debug("page status wait, will retry", key.path);
                this.retryAfterWait();
                return;
            }

            const kind = this.resolveKind(status, sh, ch, contentEnabled);
            if (!kind) {
                // Sunucuda guncel: bir daha sorma
                memo[key.pk] = [sh, ch, now()];
                writeMemo(this.siteId, memo);
                return;
            }

            await this.upload(kind, key, collected, sh, ch, kind === "full" ? content : null);

            memo[key.pk] = [sh, ch, now()];
            writeMemo(this.siteId, memo);
            this.uploads += 1;
        } finally {
            this.inFlight = false;
        }
    }

    /**
     * Sunucu durumuna gore ne gonderilecegini belirler.
     * @param {Boolean} contentEnabled markdown icerigi toplaniyor mu
     * @returns {String} "" (gonderme) | "meta" | "full" | "confirm"
     */
    resolveKind(status, sh, ch, contentEnabled) {
        const state = status.s;

        if (state === "off" || state === "wait") return "";

        if (state === "need") return contentEnabled ? "full" : "meta";

        // Aday icerik var: ayni hash ile geldiysek teyit yeterli (icerik gondermeyiz)
        if (state === "verify") {
            if (!contentEnabled) return status.sh !== sh ? "meta" : "";
            return status.ch && status.ch === ch ? "confirm" : "full";
        }

        // state === "ok": yalnizca degisiklik varsa gonder.
        // Sunucuda hic icerik yoksa (ch bos) ve biz icerik toplayabiliyorsak
        // bir kez tam gonderim yapilir; aksi halde daha once meta ile kaydedilmis
        // sayfalarin icerigi hic toplanmazdi.
        if (contentEnabled && ch && !status.ch) return "full";
        if (status.sh !== sh) return contentEnabled && status.ch && status.ch !== ch ? "full" : "meta";
        if (contentEnabled && ch && status.ch && status.ch !== ch) return "full";
        return "";
    }

    async fetchStatus(pk) {
        try {
            const url =
                this.tracker.pageStatusUrl +
                "?siteId=" +
                encodeURIComponent(this.siteId) +
                "&pk=" +
                encodeURIComponent(pk);
            const response = await fetch(url, { method: "GET", credentials: "omit" });
            if (!response.ok) return null;
            return await response.json();
        } catch (e) {
            return null;
        }
    }

    async upload(kind, key, collected, sh, ch, content) {
        const body = {
            v: 1,
            siteId: this.siteId,
            uid: identity.uid(),
            sid: identity.sid(),
            pk: key.pk,
            kind: kind,
            url: key.url,
            path: key.path,
            host: key.host,
            sh: sh,
            sentAt: now(),
            meta: collected.meta,
            jsonLd: collected.jsonLd,
        };
        if (ch) body.ch = ch;
        if (content) body.content = content;

        try {
            // sendBeacon degil: govde 64 KB kotasini asabilir
            await fetch(this.tracker.pageEndpoint, {
                method: "POST",
                headers: { "Content-Type": "text/plain" },
                body: JSON.stringify(body),
                credentials: "omit",
                keepalive: false,
            });
            log.debug("page uploaded", kind, key.path);
        } catch (e) {
            log.debug("page upload failed", e);
        }
    }
}

export default PageReporter;



//////////////////
// WEBPACK FOOTER
// ./static/src/tracker/pages/index.js
// module id = null
// module chunks = 